import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

// Status -> tailwind classes (background + text). Keep in sync with dashboard list styling.
const STATUS_STYLES = {
  pending_payment: 'bg-amber-100 text-amber-800 border-amber-300',
  awaiting_cleaner_response: 'bg-blue-100 text-blue-800 border-blue-300',
  change_proposed: 'bg-amber-100 text-amber-900 border-amber-400',
  declined_by_cleaner: 'bg-rose-100 text-rose-800 border-rose-300',
  confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  completed: 'bg-text-primary/10 text-text-primary border-text-primary/20',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-300',
  cancelled_late: 'bg-rose-100 text-rose-700 border-rose-300',
};

const DOW_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const formatYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // Sunday
  d.setHours(0, 0, 0, 0);
  return d;
};

// Returns minutes since midnight for "HH:MM" — or null
const minutesFromTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const [hh, mm] = timeStr.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

// Time-grid rendering range: 6 AM → 11 PM (1020 min) — 17 hours, 60px each
const DAY_START_MIN = 6 * 60;
const DAY_END_MIN = 23 * 60;
const HOUR_PX = 60;

const BookingsCalendar = ({ bookings, perspective = 'customer', onSelectBooking }) => {
  const { t, lang } = useLanguage();
  const today = useMemo(() => {
    const t0 = new Date();
    t0.setHours(0, 0, 0, 0);
    return t0;
  }, []);
  const [viewMode, setViewMode] = useState('month'); // 'day' | 'week' | 'month'
  const [cursor, setCursor] = useState(() => today);

  const byDate = useMemo(() => {
    const map = {};
    (bookings || []).forEach((b) => {
      if (!b.service_date) return;
      (map[b.service_date] = map[b.service_date] || []).push(b);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
    );
    return map;
  }, [bookings]);

  const counterpartyLabel = perspective === 'customer' ? 'cleaner_name' : 'customer_name';
  const todayYmd = formatYmd(today);
  const locale = lang === 'es' ? 'es-ES' : 'en-US';

  // === Header label and navigation depend on viewMode ===
  const headerLabel = useMemo(() => {
    if (viewMode === 'month') {
      return cursor.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
    if (viewMode === 'week') {
      const start = startOfWeek(cursor);
      const end = addDays(start, 6);
      const sameMonth = start.getMonth() === end.getMonth();
      const s = start.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
      const e = end.toLocaleDateString(locale, {
        month: sameMonth ? undefined : 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `${s} – ${e}`;
    }
    return cursor.toLocaleDateString(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [viewMode, cursor, locale]);

  const navStep = { month: 30, week: 7, day: 1 }[viewMode];
  const goPrev = () => {
    if (viewMode === 'month') {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    } else {
      setCursor(addDays(cursor, -navStep));
    }
  };
  const goNext = () => {
    if (viewMode === 'month') {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    } else {
      setCursor(addDays(cursor, navStep));
    }
  };
  const goToday = () => setCursor(today);

  // === Month grid cells ===
  const monthCells = useMemo(() => {
    if (viewMode !== 'month') return [];
    const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay());
    const arr = [];
    for (let i = 0; i < 42; i++) {
      arr.push(addDays(gridStart, i));
    }
    return arr;
  }, [cursor, viewMode]);

  // === Week days ===
  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return [];
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [cursor, viewMode]);

  const dayDate = viewMode === 'day' ? cursor : null;

  // === Render: shared header ===
  const renderViewSwitcher = () => (
    <div className="inline-flex bg-white border border-border rounded-full p-0.5" role="group" data-testid="calendar-view-switcher">
      {['day', 'week', 'month'].map((m) => (
        <button
          key={m}
          onClick={() => setViewMode(m)}
          className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
            viewMode === m
              ? 'bg-text-primary text-white'
              : 'text-text-secondary hover:text-text-primary'
          }`}
          aria-pressed={viewMode === m}
          data-testid={`calendar-view-${m}`}
        >
          {t(`calendar.view.${m}`, m.charAt(0).toUpperCase() + m.slice(1))}
        </button>
      ))}
    </div>
  );

  const renderBookingChip = (b, compact = true) => {
    const styles = STATUS_STYLES[b.status] || STATUS_STYLES.confirmed;
    return (
      <button
        key={b.id}
        onClick={() => onSelectBooking && onSelectBooking(b)}
        className={`text-left text-[10px] leading-tight px-1.5 py-1 rounded border ${styles} truncate hover:opacity-80 transition-opacity ${
          compact ? '' : 'w-full'
        }`}
        title={`${b.start_time || ''} ${b[counterpartyLabel] || ''}`}
        data-testid={`calendar-booking-${b.id}`}
      >
        <span className="font-semibold">{b.start_time}</span>{' '}
        <span className="truncate">{b[counterpartyLabel]}</span>
      </button>
    );
  };

  return (
    <div className="bg-surface rounded-2xl border border-border overflow-hidden" data-testid="bookings-calendar">
      {/* Top toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-muted gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            aria-label={t('calendar.prev', 'Previous')}
            className="p-2 rounded-full hover:bg-white text-text-secondary hover:text-text-primary transition-colors"
            data-testid="calendar-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 className="font-heading font-bold text-text-primary capitalize" data-testid="calendar-month-label">
            {headerLabel}
          </h3>
          <button
            onClick={goNext}
            aria-label={t('calendar.next', 'Next')}
            className="p-2 rounded-full hover:bg-white text-text-secondary hover:text-text-primary transition-colors"
            data-testid="calendar-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={goToday}
            className="text-xs text-text-secondary hover:text-primary underline-offset-2 hover:underline ml-1"
            data-testid="calendar-today"
          >
            {t('calendar.today', 'Today')}
          </button>
        </div>
        {renderViewSwitcher()}
      </div>

      {/* === MONTH === */}
      {viewMode === 'month' && (
        <>
          <div className="grid grid-cols-7 border-b border-border bg-surface-muted/40">
            {DOW_KEYS.map((dow) => (
              <div
                key={dow}
                className="px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-text-secondary text-center"
              >
                {t(`calendar.dow.${dow}`, dow.charAt(0).toUpperCase() + dow.slice(1))}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 auto-rows-fr">
            {monthCells.map((d, idx) => {
              const ymd = formatYmd(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = ymd === todayYmd;
              const dayBookings = byDate[ymd] || [];
              return (
                <div
                  key={idx}
                  className={`min-h-[88px] border-b border-r border-border p-1.5 flex flex-col gap-1 ${
                    inMonth ? 'bg-white' : 'bg-surface-muted/30'
                  }`}
                  data-testid={`calendar-cell-${ymd}`}
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => { setCursor(d); setViewMode('day'); }}
                      className={`inline-flex items-center justify-center text-xs font-semibold w-6 h-6 rounded-full hover:ring-2 hover:ring-primary ${
                        isToday ? 'bg-primary text-white' : inMonth ? 'text-text-primary' : 'text-text-secondary/60'
                      }`}
                      title={t('calendar.openDay', 'Open day view')}
                    >
                      {d.getDate()}
                    </button>
                    {dayBookings.length > 2 && (
                      <span className="text-[9px] text-text-secondary font-medium">
                        {dayBookings.length}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 overflow-hidden">
                    {dayBookings.slice(0, 3).map((b) => renderBookingChip(b))}
                    {dayBookings.length > 3 && (
                      <span className="text-[9px] text-text-secondary px-1">
                        +{dayBookings.length - 3} {t('calendar.more', 'more')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* === WEEK === */}
      {viewMode === 'week' && (
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            {/* Day headers */}
            <div className="grid grid-cols-8 border-b border-border bg-surface-muted/40 sticky top-0">
              <div className="px-2 py-2 text-[10px] text-text-secondary" />
              {weekDays.map((d) => {
                const isTodayCell = formatYmd(d) === todayYmd;
                return (
                  <button
                    key={d.toISOString()}
                    onClick={() => { setCursor(d); setViewMode('day'); }}
                    className={`px-2 py-2 text-center hover:bg-surface-muted transition-colors ${
                      isTodayCell ? 'bg-primary-light' : ''
                    }`}
                    data-testid={`calendar-week-day-${formatYmd(d)}`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">
                      {d.toLocaleDateString(locale, { weekday: 'short' })}
                    </div>
                    <div className={`text-sm font-bold ${isTodayCell ? 'text-primary' : 'text-text-primary'}`}>
                      {d.getDate()}
                    </div>
                  </button>
                );
              })}
            </div>
            {/* Time grid */}
            <div className="relative">
              <div className="grid grid-cols-8">
                {/* Hour labels column */}
                <div>
                  {Array.from({ length: (DAY_END_MIN - DAY_START_MIN) / 60 + 1 }, (_, i) => {
                    const hr = DAY_START_MIN / 60 + i;
                    return (
                      <div key={hr} className="h-[60px] text-[10px] text-text-secondary text-right pr-2 border-r border-border">
                        {hr === 12 ? '12 PM' : hr > 12 ? `${hr - 12} PM` : `${hr} AM`}
                      </div>
                    );
                  })}
                </div>
                {/* Day columns */}
                {weekDays.map((d) => {
                  const ymd = formatYmd(d);
                  const dayBookings = byDate[ymd] || [];
                  return (
                    <div
                      key={ymd}
                      className="relative border-r border-border"
                      style={{ height: `${((DAY_END_MIN - DAY_START_MIN) / 60 + 1) * HOUR_PX}px` }}
                      data-testid={`calendar-week-col-${ymd}`}
                    >
                      {/* Hour grid lines */}
                      {Array.from({ length: (DAY_END_MIN - DAY_START_MIN) / 60 + 1 }, (_, i) => (
                        <div
                          key={i}
                          className="absolute left-0 right-0 border-b border-border/60"
                          style={{ top: `${i * HOUR_PX}px`, height: `${HOUR_PX}px` }}
                        />
                      ))}
                      {/* Bookings as positioned blocks */}
                      {dayBookings.map((b) => {
                        const startMin = minutesFromTime(b.start_time || b.service_time);
                        const endMin = minutesFromTime(b.end_time) || (startMin !== null ? startMin + 120 : null);
                        if (startMin === null || endMin === null) return null;
                        const top = Math.max(0, (startMin - DAY_START_MIN) / 60) * HOUR_PX;
                        const height = Math.max(20, ((endMin - startMin) / 60) * HOUR_PX);
                        const styles = STATUS_STYLES[b.status] || STATUS_STYLES.confirmed;
                        return (
                          <button
                            key={b.id}
                            onClick={() => onSelectBooking && onSelectBooking(b)}
                            className={`absolute left-1 right-1 rounded border ${styles} px-1.5 py-1 text-[10px] text-left overflow-hidden hover:shadow-md transition-shadow`}
                            style={{ top: `${top}px`, height: `${height}px` }}
                            data-testid={`calendar-booking-${b.id}`}
                          >
                            <div className="font-semibold">
                              {b.start_time}{b.end_time ? `–${b.end_time}` : ''}
                            </div>
                            <div className="truncate">{b[counterpartyLabel]}</div>
                            <div className="truncate text-[9px] opacity-80">{b.service_type}</div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === DAY === */}
      {viewMode === 'day' && dayDate && (
        <div>
          {(() => {
            const ymd = formatYmd(dayDate);
            const dayBookings = byDate[ymd] || [];
            return (
              <div className="grid grid-cols-12">
                {/* Hour labels */}
                <div className="col-span-1">
                  {Array.from({ length: (DAY_END_MIN - DAY_START_MIN) / 60 + 1 }, (_, i) => {
                    const hr = DAY_START_MIN / 60 + i;
                    return (
                      <div key={hr} className="h-[60px] text-[10px] text-text-secondary text-right pr-2 border-r border-border">
                        {hr === 12 ? '12 PM' : hr > 12 ? `${hr - 12} PM` : `${hr} AM`}
                      </div>
                    );
                  })}
                </div>
                {/* Day column */}
                <div
                  className="col-span-11 relative"
                  style={{ height: `${((DAY_END_MIN - DAY_START_MIN) / 60 + 1) * HOUR_PX}px` }}
                  data-testid={`calendar-day-col-${ymd}`}
                >
                  {Array.from({ length: (DAY_END_MIN - DAY_START_MIN) / 60 + 1 }, (_, i) => (
                    <div
                      key={i}
                      className="absolute left-0 right-0 border-b border-border/60"
                      style={{ top: `${i * HOUR_PX}px`, height: `${HOUR_PX}px` }}
                    />
                  ))}
                  {dayBookings.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-text-secondary text-sm">
                      {t('calendar.noBookingsDay', 'No bookings on this day')}
                    </div>
                  )}
                  {dayBookings.map((b) => {
                    const startMin = minutesFromTime(b.start_time || b.service_time);
                    const endMin = minutesFromTime(b.end_time) || (startMin !== null ? startMin + 120 : null);
                    if (startMin === null || endMin === null) return null;
                    const top = Math.max(0, (startMin - DAY_START_MIN) / 60) * HOUR_PX;
                    const height = Math.max(40, ((endMin - startMin) / 60) * HOUR_PX);
                    const styles = STATUS_STYLES[b.status] || STATUS_STYLES.confirmed;
                    return (
                      <button
                        key={b.id}
                        onClick={() => onSelectBooking && onSelectBooking(b)}
                        className={`absolute left-2 right-2 rounded-lg border ${styles} px-3 py-2 text-xs text-left overflow-hidden hover:shadow-md transition-shadow`}
                        style={{ top: `${top}px`, height: `${height}px` }}
                        data-testid={`calendar-booking-${b.id}`}
                      >
                        <div className="font-bold text-sm">
                          {b.start_time}{b.end_time ? `–${b.end_time}` : ''}
                        </div>
                        <div className="font-semibold truncate">{b[counterpartyLabel]}</div>
                        <div className="text-[11px] truncate opacity-80">{b.service_type}</div>
                        {b.place_label && (
                          <div className="text-[10px] truncate opacity-70 mt-0.5">{b.place_label}</div>
                        )}
                        {/* Travel-time chip (cleaner view) */}
                        {b.travel_minutes_from_previous != null && (
                          <div
                            className={`text-[9px] mt-1 px-1.5 py-0.5 rounded inline-block ${
                              b.travel_warning
                                ? 'bg-red-100 text-red-700'
                                : 'bg-black/10 text-black/70'
                            }`}
                            data-testid={`calendar-travel-${b.id}`}
                          >
                            🚗 {b.travel_minutes_from_previous}m · {b.travel_miles_from_previous}mi
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Legend */}
      <div className="px-4 py-3 border-t border-border flex flex-wrap items-center gap-3 text-[10px]" data-testid="calendar-legend">
        {Object.entries({
          confirmed: t('dashboard.status.confirmed', 'Confirmed'),
          awaiting_cleaner_response: t('dashboard.status.awaitingCleaner', 'Awaiting cleaner'),
          change_proposed: t('dashboard.status.changeProposed', 'Change proposed'),
          pending_payment: t('dashboard.status.pendingPayment', 'Pending Payment'),
          in_progress: t('dashboard.status.inProgress', 'In Progress'),
          completed: t('dashboard.status.completed', 'Completed'),
          cancelled: t('dashboard.status.cancelled', 'Cancelled'),
        }).map(([k, label]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded border ${STATUS_STYLES[k]}`} />
            <span className="text-text-secondary">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookingsCalendar;
